/**
 * /belanja/top-up — pilih paket point + bayar via Mayar (plan 19 Fase 1).
 *
 * Flow:
 *   1. Fetch paket dari GET /v1/billing/packages (harga selalu dari server).
 *   2. Tap "Beli" → POST /v1/billing/topup (idempotency_key fresh per tap)
 *      → buka payment_url via expo-web-browser.
 *   3. Layar menunggu: poll GET /v1/billing/wallet tiap 5 detik (max 10
 *      menit). Saldo berubah hanya setelah webhook Mayar + revalidasi API
 *      lolos — redirect dari browser pembayaran tidak pernah dipercaya.
 *   4. Saldo naik → confetti (pola home) + tampilkan saldo baru.
 *
 * Guest → diblok di sini juga (defense in depth; hub sudah mencegah masuk).
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { ActivityIndicator, Pressable, ScrollView, StyleSheet, View } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { useFocusEffect, useRouter } from 'expo-router';
import * as WebBrowser from 'expo-web-browser';

import { PackageCard } from '@/components/belanja/PackageCard';
import { Confetti, type ConfettiHandle } from '@/components/home/Confetti';
import { PremiumUpgradeModal } from '@/components/premium/PremiumUpgradeModal';
import { SegeraHadirModal } from '@/components/premium/SegeraHadirModal';
import { ThemedText } from '@/components/themed-text';
import { ThemedView } from '@/components/themed-view';
import { Brand, Colors, MaxContentWidth, Radii, Spacing } from '@/constants/theme';
import { ApiError } from '@/lib/api/client';
import {
  createTopup,
  getBillingPackages,
  getWallet,
  type CreditPackage,
} from '@/lib/api/billing';
import { getStoredAccount, isGuestSession } from '@/lib/auth';
import { getPremiumPromptState, setPremiumPromptState } from '@/lib/premium-prompt';
import { trackAnalyticsEvent } from '@/lib/analytics';

const POLL_INTERVAL_MS = 5_000;
const POLL_TIMEOUT_MS = 10 * 60_000;

type State =
  | { kind: 'loading' }
  | { kind: 'ready'; packages: CreditPackage[] }
  | { kind: 'error'; message: string };

type Purchase =
  | { kind: 'idle' }
  | { kind: 'creating'; packageCode: string }
  | {
      kind: 'waiting';
      packageCode: string;
      creditAmount: number;
      amountIdr: number;
      baseline: number;
      startedAt: number;
    }
  | { kind: 'success'; balance: number }
  | { kind: 'timeout' }
  | { kind: 'failed'; message: string };

/** Idempotency key fresh per tap beli — crypto.randomUUID dengan fallback. */
function newIdempotencyKey(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `topup-${Date.now()}-${Math.random().toString(36).slice(2, 12)}`;
}

export default function TopUpScreen() {
  const router = useRouter();
  const confettiRef = useRef<ConfettiHandle>(null);
  const [state, setState] = useState<State>({ kind: 'loading' });
  const [purchase, setPurchase] = useState<Purchase>({ kind: 'idle' });
  const [loginModal, setLoginModal] = useState(false);
  const [upgradeModal, setUpgradeModal] = useState(false);
  const purchaseRef = useRef<Purchase>(purchase);
  purchaseRef.current = purchase;

  // Guest tidak boleh transaksi — tampilkan modal login, konten tetap loading.
  useEffect(() => {
    if (isGuestSession()) setLoginModal(true);
  }, []);

  useFocusEffect(
    useCallback(() => {
      if (isGuestSession()) return;
      let cancelled = false;
      getBillingPackages()
        .then((packages) => {
          if (!cancelled) setState({ kind: 'ready', packages });
        })
        .catch((err: unknown) => {
          if (cancelled) return;
          setState({
            kind: 'error',
            message: err instanceof ApiError ? err.message : 'Gagal memuat paket. Coba lagi.',
          });
        });
      return () => {
        cancelled = true;
      };
    }, []),
  );

  // Poll wallet selama menunggu webhook. Dibaca dari purchaseRef supaya
  // interval tidak di-reset tiap render.
  useEffect(() => {
    if (purchase.kind !== 'waiting') return;
    const waitingPurchase = purchase;
    const { baseline, startedAt, packageCode, creditAmount, amountIdr } = purchase;
    let finished = false;
    const timer = setInterval(() => {
      void (async () => {
        try {
          const wallet = await getWallet();
          if (finished || purchaseRef.current !== waitingPurchase) return;
          if (wallet.balance > baseline) {
            finished = true;
            clearInterval(timer);
            trackAnalyticsEvent('Credit Purchase Completed', {
              package_code: packageCode,
              credit_amount: creditAmount,
              amount_idr: amountIdr,
            });
            setPurchase({ kind: 'success', balance: wallet.balance });
            confettiRef.current?.play();
          } else if (Date.now() - startedAt > POLL_TIMEOUT_MS) {
            finished = true;
            clearInterval(timer);
            setPurchase({ kind: 'timeout' });
          }
        } catch {
          // Transient network error — interval berikutnya mencoba lagi.
        }
      })();
    }, POLL_INTERVAL_MS);
    return () => clearInterval(timer);
  }, [purchase]);

  // PremiumUpgradeModal sekali saja: saat purchase success pertama kali dan
  // user belum pernah memutuskan (pending). Keputusan disimpan per akun.
  useEffect(() => {
    if (purchase.kind !== 'success') return;
    const account = getStoredAccount();
    if (!account || account.is_guest === true) return;
    if (getPremiumPromptState(account.id) !== 'pending') return;
    setUpgradeModal(true);
  }, [purchase]);

  const onBuy = useCallback(
    async (packageCode: string) => {
      if (purchaseRef.current.kind === 'creating' || purchaseRef.current.kind === 'waiting') return;
      const selectedPackage = state.kind === 'ready'
        ? state.packages.find((pkg) => pkg.code === packageCode)
        : undefined;
      if (!selectedPackage) return;
      setPurchase({ kind: 'creating', packageCode });
      try {
        const { balance } = await getWallet();
        const result = await createTopup(packageCode, newIdempotencyKey());
        if (result.payment_url) {
          await WebBrowser.openBrowserAsync(result.payment_url).catch(() => {});
        }
        setPurchase({
          kind: 'waiting',
          packageCode,
          creditAmount: selectedPackage.credit_amount,
          amountIdr: selectedPackage.price_idr,
          baseline: balance,
          startedAt: Date.now(),
        });
      } catch (err) {
        if (err instanceof ApiError && err.code === 'AccountRequired') {
          setLoginModal(true);
          setPurchase({ kind: 'idle' });
          return;
        }
        setPurchase({
          kind: 'failed',
          message: err instanceof ApiError ? err.message : 'Gagal membuat pembayaran. Coba lagi.',
        });
      }
    },
    [state],
  );

  const onPressLogin = useCallback(() => {
    setLoginModal(false);
    router.replace('/login' as never);
  }, [router]);

  const onUpgradeChoose = useCallback(() => {
    const account = getStoredAccount();
    setPremiumPromptState(account?.id ?? null, 'completed');
    setUpgradeModal(false);
    router.push('/akun/edit-preferensi' as never);
  }, [router]);

  const onUpgradeLater = useCallback(() => {
    const account = getStoredAccount();
    setPremiumPromptState(account?.id ?? null, 'dismissed');
    setUpgradeModal(false);
  }, []);

  const busy = purchase.kind === 'creating' || purchase.kind === 'waiting';
  const processingPackageCode =
    purchase.kind === 'creating' || purchase.kind === 'waiting' ? purchase.packageCode : null;

  return (
    <ThemedView style={styles.root}>
      <SafeAreaView style={styles.safe} edges={['top', 'left', 'right']}>
        <View style={styles.header}>
          <Pressable
            onPress={() => router.back()}
            style={({ pressed }) => [styles.backBtn, pressed && styles.pressed]}
            accessibilityRole="button"
            accessibilityLabel="Kembali"
          >
            <ThemedText style={styles.backArrow}>←</ThemedText>
          </Pressable>
          <ThemedText type="subtitle" style={styles.headerTitle}>Top up point</ThemedText>
          <View style={styles.headerSpacer} />
        </View>

        <ScrollView contentContainerStyle={styles.content} showsVerticalScrollIndicator={false}>
          <View style={styles.container}>
            {state.kind === 'loading' && (
              <View style={styles.centerBox}>
                <ActivityIndicator color={Brand.primary} />
                <ThemedText themeColor="textSecondary" style={styles.centerText}>
                  Memuat paket…
                </ThemedText>
              </View>
            )}

            {state.kind === 'error' && (
              <View style={styles.centerBox}>
                <ThemedText style={styles.errorText}>{state.message}</ThemedText>
              </View>
            )}

            {state.kind === 'ready' && (
              <>
                <ThemedText themeColor="textSecondary" style={styles.intro}>
                  Pilih paket point. Pembayaran diproses aman lewat Mayar — saldo
                  masuk otomatis setelah pembayaran terkonfirmasi.
                </ThemedText>
                <View style={styles.packageList}>
                  {state.packages.map((pkg) => (
                    <PackageCard
                      key={pkg.code}
                      pkg={pkg}
                      disabled={busy}
                      isProcessing={processingPackageCode === pkg.code}
                      onBuy={onBuy}
                    />
                  ))}
                </View>
              </>
            )}

            {purchase.kind === 'waiting' && (
              <View style={styles.waitingCard}>
                <ActivityIndicator color={Brand.primary} />
                <ThemedText style={styles.waitingTitle}>Menunggu pembayaran…</ThemedText>
                <ThemedText themeColor="textSecondary" style={styles.waitingBody}>
                  Selesaikan pembayaran di halaman Mayar. Saldo point akan masuk
                  otomatis setelah pembayaran terkonfirmasi — halaman ini dicek
                  tiap beberapa detik.
                </ThemedText>
                <Pressable
                  onPress={() => setPurchase({ kind: 'idle' })}
                  style={({ pressed }) => [styles.ghostBtn, pressed && styles.pressed]}
                  accessibilityRole="button"
                  accessibilityLabel="Sembunyikan dan cek nanti"
                >
                  <ThemedText style={styles.ghostBtnText}>Cek nanti di Riwayat</ThemedText>
                </Pressable>
              </View>
            )}

            {purchase.kind === 'success' && (
              <View style={styles.successCard}>
                <ThemedText style={styles.successEmoji}>🎉</ThemedText>
                <ThemedText style={styles.waitingTitle}>Top up berhasil!</ThemedText>
                <ThemedText themeColor="textSecondary" style={styles.waitingBody}>
                  Saldo point Bunda sekarang {purchase.balance}.
                </ThemedText>
              </View>
            )}

            {purchase.kind === 'timeout' && (
              <View style={styles.waitingCard}>
                <ThemedText style={styles.waitingTitle}>Belum ada konfirmasi</ThemedText>
                <ThemedText themeColor="textSecondary" style={styles.waitingBody}>
                  Pembayaran belum terkonfirmasi dalam 10 menit. Kalau sudah bayar,
                  saldo akan masuk otomatis — cek Riwayat Transaksi nanti.
                </ThemedText>
                <Pressable
                  onPress={() => router.push('/belanja/transaksi' as never)}
                  style={({ pressed }) => [styles.ghostBtn, pressed && styles.pressed]}
                  accessibilityRole="button"
                  accessibilityLabel="Lihat riwayat transaksi"
                >
                  <ThemedText style={styles.ghostBtnText}>Lihat Riwayat Transaksi</ThemedText>
                </Pressable>
              </View>
            )}

            {purchase.kind === 'failed' && (
              <View style={styles.waitingCard}>
                <ThemedText style={styles.errorText}>{purchase.message}</ThemedText>
                <Pressable
                  onPress={() => setPurchase({ kind: 'idle' })}
                  style={({ pressed }) => [styles.ghostBtn, pressed && styles.pressed]}
                  accessibilityRole="button"
                  accessibilityLabel="Tutup pesan error"
                >
                  <ThemedText style={styles.ghostBtnText}>Tutup</ThemedText>
                </Pressable>
              </View>
            )}
          </View>
        </ScrollView>

        <Confetti ref={confettiRef} />
      </SafeAreaView>

      <SegeraHadirModal
        visible={loginModal}
        onClose={() => setLoginModal(false)}
        title="Masuk untuk Mulai Belanja"
        body="Masuk dengan Google dulu untuk top up point dan mulai belanja. Progress Bunda tetap tersimpan."
        ctaLabel="Masuk dengan Google"
        onCtaPress={onPressLogin}
      />

      <PremiumUpgradeModal
        visible={upgradeModal}
        onChoose={onUpgradeChoose}
        onLater={onUpgradeLater}
      />
    </ThemedView>
  );
}

const styles = StyleSheet.create({
  root: { flex: 1, backgroundColor: '#fcfbf8' },
  safe: { flex: 1 },
  header: {
    alignItems: 'center',
    borderBottomColor: '#e5e5e5',
    borderBottomWidth: 1,
    flexDirection: 'row',
    paddingHorizontal: Spacing.three,
    paddingVertical: Spacing.two,
  },
  backBtn: { alignItems: 'center', height: 40, justifyContent: 'center', width: 40 },
  backArrow: { color: Brand.primary, fontSize: 24, fontWeight: '600' },
  headerTitle: { color: Colors.light.text, flex: 1, fontSize: 18, fontWeight: '700', textAlign: 'center' },
  headerSpacer: { width: 40 },
  content: { paddingBottom: Spacing.six },
  container: {
    alignSelf: 'center',
    gap: Spacing.four,
    maxWidth: MaxContentWidth,
    paddingHorizontal: Spacing.four,
    paddingTop: Spacing.four,
    width: '100%',
  },
  centerBox: { alignItems: 'center', gap: Spacing.two, paddingVertical: Spacing.six },
  centerText: { fontSize: 13 },
  errorText: { color: '#b3261e', fontSize: 14, lineHeight: 20, textAlign: 'center' },
  intro: { fontSize: 13, lineHeight: 20 },
  packageList: { gap: Spacing.three },
  waitingCard: {
    alignItems: 'center',
    backgroundColor: '#ffffff',
    borderColor: '#e5e5e5',
    borderRadius: Radii.medium,
    borderWidth: 1,
    gap: Spacing.two,
    padding: Spacing.four,
  },
  waitingTitle: { color: Colors.light.text, fontSize: 17, fontWeight: '700', textAlign: 'center' },
  waitingBody: { fontSize: 13, lineHeight: 20, textAlign: 'center' },
  successCard: {
    alignItems: 'center',
    backgroundColor: '#e6f4ea',
    borderRadius: Radii.medium,
    gap: Spacing.two,
    padding: Spacing.four,
  },
  successEmoji: { fontSize: 36 },
  ghostBtn: {
    borderColor: Brand.primary,
    borderRadius: Radii.pill,
    borderWidth: 1,
    marginTop: Spacing.one,
    paddingHorizontal: Spacing.three,
    paddingVertical: Spacing.two,
  },
  ghostBtnText: { color: Brand.primary, fontSize: 13, fontWeight: '600' },
  pressed: { opacity: 0.75 },
});
